const ENTITY_ESCAPES = [
    [/&/g, '&amp;'],
    [/</g, '&lt;'],
    [/>/g, '&gt;'],
    [/"/g, '&quot;'],
    [/'/g, '&#39;'],
];
/**
 * Does this look like the HTML our own write pipeline produces (Tiptap's
 * `getHTML()`, sanitized server-side) rather than a legacy or CSV-imported
 * plain-text description?
 *
 * The write pipeline always serializes a document starting with a block
 * element — `<p>`, `<ul>` or `<ol>` — and the sanitizer preserves that. Plain
 * text that happens to start with a literal `<p...>` is the only
 * false-positive case, and it is safe: it just gets treated as (and
 * re-sanitized into) rich content instead of being escaped.
 *
 * This same predicate gates both the write-time sanitizer (apps/api) and
 * read-time rendering (apps/web), so a value is never escaped twice or
 * trusted inconsistently.
 */
export function isRichTextHtml(value) {
    return /^\s*<(p|ul|ol)[\s>]/i.test(value);
}
/**
 * Renders a stored `description` as HTML for display.
 *
 * Already-rich values pass through unchanged — they were allowlist-sanitized
 * at write time, so they are trusted here. Legacy plain text is entity-escaped
 * and turned into paragraphs (blank line = new paragraph) and line breaks
 * (single newline), the same shape a rich editor would have produced.
 */
export function descriptionToHtml(value) {
    if (isRichTextHtml(value))
        return value;
    const escaped = ENTITY_ESCAPES.reduce((s, [pattern, repl]) => s.replace(pattern, repl), value);
    const paragraphs = escaped
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);
    if (paragraphs.length === 0)
        return '';
    return paragraphs.map((p) => `<p>${p.replace(/\n/g, '<br />')}</p>`).join('');
}
/**
 * Reduces a stored `description` to plain text, for contexts that must never
 * see markup: SEO metadata, the product card's one-line fallback, and the
 * vending terminal payload (a dumb terminal has no HTML renderer).
 */
export function descriptionToText(value) {
    if (value == null)
        return null;
    if (!isRichTextHtml(value))
        return value;
    const withBreaks = value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|li)>/gi, '\n')
        .replace(/<[^>]+>/g, '');
    const decoded = withBreaks
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
    const collapsed = decoded.replace(/\n{3,}/g, '\n\n').trim();
    return collapsed || null;
}
//# sourceMappingURL=rich-text.js.map